import { useState, useEffect } from 'react';
import { graphql, fetchQuery, useRelayEnvironment } from 'react-relay';

import { getBuyerId } from 'dibs-cookie-jar';
import { getAbTestV2, trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';

const BOMT_ORDER_ACTION_NOTIFICATIONS = 'bomt-order-action-notifications';

import { type useHasUnreadNotificationsQuery } from './__generated__/useHasUnreadNotificationsQuery.graphql.ts';

let isTestTracked = false;

const trackBomtOrderActionNotificationsVariant = (): void => {
    if (!isTestTracked) {
        isTestTracked = true;
        trackAbTestV2Variant(BOMT_ORDER_ACTION_NOTIFICATIONS);
    }
};

const query = graphql`
    query useHasUnreadNotificationsQuery($userId: String!) {
        viewer {
            user(userId: $userId) {
                conversationSummary {
                    unread
                    unreadActiveOffers
                }
            }
            actionRequiredOrders: transactions(
                viewName: "buyer.bomtActionRequired"
                buyerId: [$userId]
            ) {
                totalResults
                edges {
                    node {
                        serviceId
                    }
                }
            }
        }
    }
`;

export const useHasUnreadNotifications = ({
    hasRootPendingRefetch,
}: {
    hasRootPendingRefetch: boolean;
}): {
    hasUnreadNotifications: boolean;
    unreadNotificationsCount: number;
    unreadOrdersNotificationsCount: number;
    unreadOrdersNotificationsOrderIds: string[];
    isBomtOrderActionNotificationsVariant: boolean;
} => {
    const environment = useRelayEnvironment();

    const [userId, setUserId] = useState('');
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
    const [unreadOrdersNotificationsCount, setUnreadOrdersNotificationsCount] = useState(0);
    const [unreadOrdersNotificationsOrderIds, setUnreadOrdersNotificationsOrderIds] = useState<
        string[]
    >([]);
    const [isBomtOrderActionNotificationsVariant, setIsBomtOrderActionNotificationsVariant] =
        useState(false);

    useEffect(() => {
        if (typeof document !== 'undefined') {
            setUserId(getBuyerId(document.cookie));
            setIsBomtOrderActionNotificationsVariant(
                getAbTestV2(BOMT_ORDER_ACTION_NOTIFICATIONS)?.variant === 'variant'
            );
        }
    }, []);

    useEffect(() => {
        if (hasRootPendingRefetch || !userId || !getAbTestV2(BOMT_ORDER_ACTION_NOTIFICATIONS)) {
            return;
        }

        fetchQuery<useHasUnreadNotificationsQuery>(environment, query, { userId })
            .toPromise()
            .then(data => {
                const { user, actionRequiredOrders } = data?.viewer || {};
                const { conversationSummary } = user || {};
                const totalUnread = conversationSummary?.unread || 0;
                const unreadOffers = conversationSummary?.unreadActiveOffers || 0;
                const actionRequiredOrdersCount = actionRequiredOrders?.totalResults || 0;
                const unreadCount = totalUnread + unreadOffers + actionRequiredOrdersCount;
                const actionRequiredOrdersIds = (actionRequiredOrders?.edges || []).map(
                    edge => edge?.node?.serviceId || ''
                );
                setHasUnreadNotifications(!!unreadCount);
                setUnreadNotificationsCount(unreadCount);
                setUnreadOrdersNotificationsCount(actionRequiredOrdersCount);
                setUnreadOrdersNotificationsOrderIds(actionRequiredOrdersIds);

                if (actionRequiredOrdersCount) {
                    trackBomtOrderActionNotificationsVariant();
                }
            });
    }, [userId, environment, isBomtOrderActionNotificationsVariant, hasRootPendingRefetch]);

    return {
        hasUnreadNotifications,
        unreadNotificationsCount,
        unreadOrdersNotificationsCount,
        unreadOrdersNotificationsOrderIds,
        isBomtOrderActionNotificationsVariant,
    };
};
